
/** @locked-file Do not modify without explicit instruction */
import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Feather, Home, Link, User, Briefcase, MicOff, X, History, ShieldCheck, Heart, CircleDollarSign, Award, UserCheck, Sparkles } from 'lucide-react';
import { ASSETS } from '../constants';

interface TrainingItem {
  date: string;
  title: string;
  category: 'long' | 'short';
}

interface FocusArea {
  id: string;
  title: string;
  engLabel: string;
  shortDesc: string;
  fullDesc: string;
  icon: React.ReactNode;
  themeColor: string; // 仅用于顶部装饰条和图标背景
}

const FOCUS_AREAS: FocusArea[] = [
  {
    id: 'shame',
    title: '羞耻感',
    engLabel: 'Shame',
    shortDesc: '把自己包裹在洋葱里不想被发现...',
    fullDesc: '羞耻感是藏得非常深的情绪感受，就像把自己包裹在洋葱里不想被任何人发现，对自己在意的人和事不敢靠近。在咨询中，让我们一层一层地对情绪、对底层愿望进行工作，让你重新感受到自己是重要的、是值得被爱的。',
    icon: <Feather size={22} />,
    themeColor: 'bg-rose-200'
  },
  {
    id: 'family',
    title: '原生家庭',
    engLabel: 'Family Roots',
    shortDesc: '也许无法原谅，但可以学会划定边界...',
    fullDesc: '“幸运的人一生被童年治愈，不幸的人一生都在治愈童年。”也许我们无法改变原生家庭、亦不准备原谅，但我们可以学会划定边界、选择回到被困住的过去、看见那个曾经很羸弱的小孩，去认可ta已经尽力了。',
    icon: <Home size={22} />,
    themeColor: 'bg-amber-200'
  },
  {
    id: 'attachment',
    title: '依恋模式',
    engLabel: 'Attachment',
    shortDesc: '涉及依恋、分离与哀伤的关系底色...',
    fullDesc: '依恋模式涉及到依恋、分离、哀伤。它和早期的母婴关系或成年后的亲密关系息息相关。不安全依恋可能有多种表现形式，如较难进入亲密关系、无意识破坏关系、对关系距离感到焦虑。我们去感受依恋的形成，寻找更自由联结方式。',
    icon: <Link size={22} />,
    themeColor: 'bg-sage-200'
  },
  {
    id: 'self',
    title: '人际自我',
    engLabel: 'Self & Others',
    shortDesc: '当自体破碎，或感到他人负面评价时...',
    fullDesc: '总觉得自己不够好，敏感于他人负面评价，难以信任他人。这些议题往往关乎自尊、自恋及自体的整合。在咨询室这个不被评价的“第三空间”，映照出自我形象，修补破碎自我。',
    icon: <User size={22} />,
    themeColor: 'bg-stone-200'
  },
  {
    id: 'workplace',
    title: '职场困惑',
    engLabel: 'Workplace',
    shortDesc: '你与工作的关系，也是内在关系的投射...',
    fullDesc: '职场关系也是关系的一种：你与工作的关系、与同事及领导的关系。如频繁换工作、对领导不耐受、感到被剥削等，也许与内在权威客体互动有关。告别扭曲的投射，职场亦可成为自我实现的疆域。',
    icon: <Briefcase size={22} />,
    themeColor: 'bg-blue-100'
  },
  {
    id: 'control',
    title: '被控制与被忽视',
    engLabel: 'Invisible Pain',
    shortDesc: '争取生命的主动权，让声音被听见...',
    fullDesc: '当我们感觉被工具化，我们的声音不被听见，我们的意志不被尊重，我们一直很辛苦地在争夺生命主动权。看见被压抑的愤怒，夺回自己的边界。',
    icon: <MicOff size={22} />,
    themeColor: 'bg-rose-100'
  }
];

const TRAINING_HISTORY: TrainingItem[] = [
  { date: '2021/04 - 2021/10', title: '简单心理Uni · Seed训练', category: 'long' },
  { date: '2022/04 - 2022/12', title: '简单心理Uni 心理咨询师培养计划 · 中阶训练', category: 'long' },
  { date: '2023/01 - 2023/09', title: '简单心理Uni 心理咨询师培养计划 · 高阶训练', category: 'long' },
  { date: '2024/06 - 2025/12', title: '第三届中国克莱因学派连续培训项目', category: 'long' },
  { date: '2026/03 - 2026/09', title: '成都市第四人民医院半年制脱产进修', category: 'long' },
  { date: '2022/03-2022/03', title: '心理咨询与治疗专业伦理（初阶）', category: 'short' },
  { date: '2023/08-2023/11', title: '依恋理论的临床实践专题课', category: 'short' },
  { date: '2024/01-2024/04', title: '哀伤治疗与创伤干预专题课', category: 'short' },
];

export const About: React.FC = () => {
  const [activeFocus, setActiveFocus] = useState<FocusArea | null>(null);

  const longTermTraining = TRAINING_HISTORY.filter(t => t.category === 'long');
  const shortTermTraining = TRAINING_HISTORY.filter(t => t.category === 'short');

  return (
    <>
      <SectionWrapper>
        {/* 1. 头部介绍区域 */}
        <div className="flex flex-col md:flex-row items-start gap-12 md:gap-16 mb-24 animate-fade-in">
          <div className="w-full md:w-1/3 shrink-0 relative group">
            <div className="bg-white rounded-sm shadow-sm relative z-10 p-3 pb-16 border border-stone-200 transition-all duration-700">
               <div className="aspect-[4/5] overflow-hidden bg-stone-50 mb-4">
                 <img 
                   src={ASSETS.profilePhoto} 
                   alt="陈瑞雪" 
                   className="w-full h-full object-cover filter contrast-[1.05]" 
                 />
               </div>
               <div className="absolute bottom-0 left-0 right-0 h-16 flex flex-col items-center justify-center overflow-hidden pointer-events-none">
                  <div className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-700 flex flex-col items-center">
                     <p className="text-stone-500 font-serif tracking-[0.5em] text-[10px] md:text-xs uppercase font-bold">看见即改变</p>
                     <div className="w-4 h-[1px] bg-rose-300 mt-2"></div>
                  </div>
               </div>
            </div>
            <div className="absolute inset-0 bg-stone-200/50 translate-x-3 translate-y-3 rounded-sm -z-0"></div>
          </div>

          <div className="w-full md:w-2/3">
            <div className="flex flex-col md:flex-row md:items-baseline gap-4 mb-10">
              <h2 className="font-serif text-4xl text-stone-900 font-bold tracking-tight">陈瑞雪</h2>
              <span className="text-sage-700 font-serif text-sm font-medium tracking-wide">精神分析动力取向 / 简单心理入驻咨询师</span>
            </div>
            
            <div className="prose prose-stone text-stone-800 font-serif leading-[2.2] text-justify space-y-8 max-w-none text-lg">
               <p>或许当你看到这段文字时，我们的关系已经开始了。我相信你给予了自己一定勇气踏上这段旅程，这将是一段<span className="text-stone-900 font-bold border-b border-rose-100">螺旋上升</span>的过程，我们都会深度参与其中。经历爱、经历恨、经历丧失与修复，将咨询中获得的安全体验和自由空间泛化到真实生活。</p>
               <p>在咨询一开始我很关注你，你怎么了？为什么走入咨询室？什么将你困住了？后来我会关注关系，你过去的关系？现在的关系？我们此时此刻的关系？到最后你会越来越熟练使用这个空间、这段关系、这个客体，我们会一起经历走向真实所要接受的哀悼、所要完成的整合。</p>
               <p>动力学的咨询像是<span className="text-stone-900 font-bold">“瞻之在前、忽焉在后”</span>的过程，时而我快半步引导你思考，时而我慢半步关注着你的探索，无论何时我都尽量陪伴着你、想要接住你。</p>
               <p>
                 最后，我关注的是关系本身及来访固有模式。我很同意动力学在框架内工作，以保障稳定性与潜意识出现；也认可关系流派“大部分问题都是<span className="text-stone-900 font-bold border-b border-rose-100">关系问题</span>”，我希望在某种程度将两者结合，在安全空间中让重复模式浮现，通过咨访关系给来访修正性体验、理解来访无法言说的困扰，在一次次西西弗斯的循环中找到出口。
               </p>
            </div>
          </div>
        </div>

        {/* 2. 核心卡片展示 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          <div className="bg-sage-50/60 p-8 rounded-sm border border-sage-200">
             <h4 className="font-serif text-lg font-bold text-stone-900 mb-6 flex items-center gap-3 border-b border-sage-300 pb-3"><ShieldCheck size={18} className="text-sage-600" /> 咨询信息</h4>
             <ul className="space-y-4 text-stone-700 font-serif text-base leading-relaxed">
               <li className="flex items-start gap-2"><span className="text-sage-400 mt-2 w-1.5 h-1.5 rounded-full bg-sage-400 shrink-0"></span><span>精神分析动力取向</span></li>
               <li className="flex items-start gap-2"><span className="text-sage-400 mt-2 w-1.5 h-1.5 rounded-full bg-sage-400 shrink-0"></span><span>持续一周 2 次个人体验</span></li>
               <li className="flex items-start gap-2"><span className="text-sage-400 mt-2 w-1.5 h-1.5 rounded-full bg-sage-400 shrink-0"></span><span>持续接受个体督导</span></li>
             </ul>
          </div>
          <div className="bg-amber-50/50 p-8 rounded-sm border border-amber-200">
             <h4 className="font-serif text-lg font-bold text-stone-900 mb-6 flex items-center gap-3 border-b border-amber-300 pb-3"><Heart size={18} className="text-amber-600" /> 咨询风格</h4>
             <p className="text-stone-700 font-serif text-base leading-loose italic">想要看到对面真实的人、体验关系互动、创造安全空间。我希望我的咨询风格是自在、温柔与放松的，也希望在此过程与你共同成长。</p>
          </div>
          <div className="bg-rose-50/60 p-8 rounded-sm border border-rose-200">
             <h4 className="font-serif text-lg font-bold text-stone-900 mb-6 flex items-center gap-3 border-b border-rose-300 pb-3"><CircleDollarSign size={18} className="text-rose-500" /> 咨询收费</h4>
             <div className="space-y-5 text-stone-700 font-serif">
                <div className="flex items-baseline justify-between border-b border-rose-200/50 pb-2"><span className="text-stone-500 text-[10px] font-mono uppercase tracking-widest font-bold">Fee</span><span className="text-xl text-stone-900 font-bold">300元 / 次</span></div>
                <div className="flex items-baseline justify-between"><span className="text-stone-500 text-[10px] font-mono uppercase tracking-widest font-bold">Experience</span><span className="text-stone-800 text-base">个案小时数 600H+</span></div>
             </div>
          </div>
        </div>

        {/* 3. 其他情况说明 */}
        <div className="mb-20 animate-fade-in max-w-4xl">
          <h3 className="font-serif text-xl text-stone-900 font-bold tracking-widest mb-10 flex items-center gap-4"><UserCheck size={20} className="text-rose-400" />其他情况说明</h3>
          <div className="space-y-10 font-serif text-stone-800 text-lg leading-[2.2] text-justify">
             <p>1、有严重精神类疾病或高风险自杀人群已超过我胜任力范围，请选择精神科医生或成熟咨询师。</p>
             <p>2、我无法给你即时性的建议、亦不擅长几次咨询就令你感觉问题都被解决。咨访关系与现实关系的对照需要时间去映照，甚至后期我们可能会呆在痛苦与不确定性中。但也就是在一次次退后又前进的过程中，我们的人格灵活度在发生着变化。</p>
          </div>
        </div>

        {/* 4. 受训背景 */}
        <div className="mb-24 pt-16 border-t border-stone-200">
          <div className="flex items-center gap-4 mb-16">
             <h3 className="font-serif text-2xl text-stone-800 font-bold flex items-center gap-3 shrink-0"><History size={24} className="text-sage-600" />受训背景</h3>
             <div className="h-px bg-stone-200 flex-grow"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
             <div className="space-y-10">
                <div className="flex items-center gap-3 mb-4"><Award size={18} className="text-amber-500" /><h4 className="font-serif text-lg text-stone-900 font-bold">长程系统培训</h4></div>
                <div className="space-y-0 pl-8 border-l border-sage-200 ml-2">
                   {longTermTraining.map((item, idx) => (
                      <div key={idx} className="relative pb-10 last:pb-0">
                         <div className="absolute -left-[35px] top-1.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-sage-400 z-10"></div>
                         <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm hover:border-sage-300 hover:shadow-md transition-all">
                            <span className="text-[10px] font-mono text-sage-600 block mb-2 tracking-widest uppercase font-black">{item.date}</span>
                            <h5 className="font-serif text-stone-900 text-base font-bold">{item.title}</h5>
                         </div>
                      </div>
                   ))}
                </div>
             </div>
             <div className="space-y-10">
                <div className="flex items-center gap-3 mb-4"><Award size={18} className="text-sage-500" /><h4 className="font-serif text-lg text-stone-900 font-bold">短期专项培训</h4></div>
                <div className="space-y-4 pl-8 border-l border-stone-200 ml-2">
                  {shortTermTraining.map((item, idx) => (
                    <div key={idx} className="relative pb-6 group">
                      <div className="absolute -left-[35px] top-1.5 w-2 h-2 rounded-full bg-stone-300 group-hover:bg-sage-500 transition-colors"></div>
                      <div className="flex flex-col md:flex-row md:items-baseline justify-between gap-4 border-b border-stone-100 pb-6">
                        <h5 className="font-serif text-stone-800 text-base font-medium">{item.title}</h5>
                        <span className="text-[10px] font-mono text-stone-500 whitespace-nowrap">{item.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
             </div>
          </div>
        </div>

        {/* 5. 关注议题区块 */}
        <div className="mb-10 pt-16 border-t border-stone-200">
          <div className="text-center mb-20">
             <h3 className="font-serif text-3xl text-stone-900 font-bold inline-block relative tracking-widest">关注议题<div className="absolute -bottom-2 left-0 right-0 h-1.5 bg-rose-200/60 -z-10"></div></h3>
             <p className="text-stone-400 text-[10px] mt-4 tracking-[0.4em] uppercase font-mono font-bold">Clinical Focus Areas</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
             {FOCUS_AREAS.map((area) => (
               <div 
                 key={area.id} 
                 onClick={() => setActiveFocus(area)} 
                 className={`
                   group bg-white px-8 py-8 md:px-12 md:py-10 rounded-sm relative
                   shadow-[0_8px_25px_-12px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)] 
                   transition-all duration-700 cursor-pointer flex flex-col items-center text-center
                   border border-stone-100
                 `}
               >
                  {/* 顶部统一装饰条 */}
                  <div className={`absolute top-0 left-0 right-0 h-[3px] ${area.themeColor} opacity-70 group-hover:opacity-100 transition-opacity`}></div>

                  {/* 右上角浮动装饰 */}
                  <div className="absolute top-5 right-5 text-stone-100 group-hover:text-rose-200/80 transition-colors duration-700">
                    <Sparkles size={14} />
                  </div>

                  {/* 图标容器 */}
                  <div className={`w-14 h-14 ${area.themeColor.replace('bg-', 'bg-opacity-10 bg-')} rounded-full flex items-center justify-center text-stone-600 group-hover:text-stone-900 transition-all duration-700 mb-6`}>
                    {area.icon}
                  </div>
                  
                  <h4 className="font-serif text-2xl text-stone-900 mb-3 font-bold tracking-widest group-hover:translate-y-[-1px] transition-transform duration-500">{area.title}</h4>
                  
                  <p className="text-[14px] text-stone-600 leading-relaxed font-serif italic px-2 font-medium opacity-90 group-hover:opacity-100 transition-opacity">
                    {area.shortDesc}
                  </p>
               </div>
             ))}
          </div>
        </div>
      </SectionWrapper>

      {/* 将弹窗移动到 SectionWrapper 外部，解决 transform 导致的 fixed 定位问题 */}
      {activeFocus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in p-4 bg-stone-900/40 backdrop-blur-sm" onClick={() => setActiveFocus(null)}>
           <div className="bg-[#fffefc] w-full max-w-2xl rounded-sm shadow-2xl relative p-8 md:p-14 border border-stone-200 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <button onClick={() => setActiveFocus(null)} className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors p-2 bg-stone-100 rounded-full"><X size={24} /></button>
              <div className="flex items-center gap-6 mb-8 pb-8 border-b border-stone-100">
                 <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center text-rose-400 shadow-sm shrink-0">{activeFocus.icon}</div>
                 <div>
                    {/* 调整：标题字重从 black 降为 bold，颜色更柔和 */}
                    <h3 className="font-serif text-2xl md:text-3xl text-stone-800 font-bold tracking-widest">{activeFocus.title}</h3>
                    <p className="text-xl md:text-2xl text-stone-400 font-handwriting mt-1">{activeFocus.engLabel}</p>
                 </div>
              </div>
              {/* 
                  调整：正文字体优化
                  - font-medium -> font-normal (常规)
                  - text-stone-800 -> text-stone-700
                  - 保持行高 leading-[2.2]
              */}
              <p className="font-serif text-stone-700 leading-[2.2] text-justify text-lg md:text-xl font-normal">
                 {activeFocus.fullDesc}
              </p>
              <div className="mt-10 flex justify-center">
                <button onClick={() => setActiveFocus(null)} className="text-[10px] font-mono tracking-widest uppercase text-stone-500 hover:text-rose-500 border-b border-stone-200 pb-1 transition-colors">Back to Overview</button>
              </div>
           </div>
        </div>
      )}
    </>
  );
};
